import { memo } from "react";
import Tooltip from "../../../Tooltip/Tooltip";
import type { JobLocation } from "../../DirectApplyTypes";
import { getScreenByStep } from "../../constants";
import styles from "./Header.module.scss";

interface HeaderProps {
    jobName: string;
    companyName: string;
    locations: JobLocation[];
    isRepeatUser: boolean;
    step: number;
    steps: number;
}

/**
 * The job's company, location(s), as well as if you have saved info. Part of the Header.
 *
 * @returns {ReactElement} The details within the header.
 */
const Details = memo((props: HeaderProps) => {
    const { companyName, locations, isRepeatUser, step, steps } = props;
    const stringLocations = locations.map((loc: JobLocation) => loc.value)
    const [first, ...rest] = stringLocations;

    const tooltipProps = {
        content: rest,
        title: "locationsTip",
        triggerText: `(+${rest.length} more)`
    };

    return (
        <div className={styles.details}>{companyName}
            <span className={styles.separator}>&bull;</span>
            <span className={styles.location}>{first}</span>
            {rest.length ? <Tooltip {...tooltipProps} /> : null}
            {isRepeatUser && step <= steps ? <p>We&apos;ve saved your application info from your most recent application.</p> : null}
        </div>
    );
});
Details.displayName = "Details";

/**
 * create a header component that displays either the job details or the final step of the application process,
 * depending on the current step.
 *
 * @returns {ReactElement} The header of the direct apply modal.
 */
const Header = memo((props: HeaderProps) => {
    const { step, steps, jobName } = props;
    const screen = getScreenByStep(step);
    return (
        <header className={styles.header}>
            <p tabIndex={1} className={styles.jobTitle}>
                {screen.name === "apply_confirmation" ? "Your application has been submitted!" : jobName}
            </p>
            {step !== steps && <Details {...props} />}
        </header>
    );
});
Header.displayName = "Header";

export default Header;
export type { HeaderProps };
